import type { ReactNode } from "react";
import type { ProvenancedValue } from "../api/types.js";
import {
  PROVENANCE_LABEL,
  classify,
  describe,
  tally,
} from "../lib/provenance.js";
import { URGENCY_WORD, describeRemaining, urgencyOf } from "../lib/deadlines.js";

/**
 * The chip that says where a number came from.
 *
 * This is the product's whole argument compressed into one element, so it is
 * worth being exact about the markup. The class carries the colour, the text
 * carries the source, and the `title` carries the full sentence — because
 * colour alone would leave a colour-blind assessor with three identical grey
 * pills, and a chip reading "Default" does not say whose default it is.
 */
export function ProvenanceChip({
  value,
}: {
  readonly value: ProvenancedValue;
}): ReactNode {
  const klass = classify(value);
  const label = PROVENANCE_LABEL[value.provenance] ?? value.provenance;
  return (
    <span className={`prov prov-${klass}`} title={describe(value)}>
      {value.confirmedBySurveyor ? `${label} · confirmed` : label}
      <span className="visually-hidden">. {describe(value)}</span>
    </span>
  );
}

/**
 * A measurement and its provenance as one unit.
 *
 * Kept together deliberately: the number and where it came from are a single
 * fact, and a table that puts them in separate columns invites somebody to read
 * down the numbers without ever looking at the sources.
 */
export function ProvenancedNumber({
  value,
  format,
}: {
  readonly value: ProvenancedValue;
  readonly format: (n: number) => string;
}): ReactNode {
  const klass = classify(value);
  return (
    <span className={`value value-${klass}`}>
      <span className="n">{format(value.value)}</span> <ProvenanceChip value={value} />
    </span>
  );
}

/**
 * How much of a survey rests on assumption.
 *
 * The single most useful number on the survey screen: a heat loss built from
 * forty measured surfaces and two assumed ones is a different object from one
 * built the other way round, and nothing else on screen says so at a glance.
 */
export function ProvenanceTally({
  values,
}: {
  readonly values: readonly ProvenancedValue[];
}): ReactNode {
  const counts = tally(values);
  if (counts.total === 0) return null;
  return (
    <p className="tally">
      <span className="count prov-confirmed">{counts.confirmed} confirmed</span>{" "}
      <span className="count prov-measured">{counts.measured} measured</span>{" "}
      <span className="count prov-assumed">{counts.assumed} assumed</span>{" "}
      <span className="muted small">of {counts.total} values</span>
    </p>
  );
}

/**
 * How close a compliance clock is to running out.
 *
 * The word is not decoration. Some of the people who read this board every day
 * cannot tell the amber row from the red one, and it gets printed in black and
 * white for site meetings, so the status has to survive losing its colour.
 */
export function UrgencyBadge({
  daysRemaining,
}: {
  readonly daysRemaining: number;
}): ReactNode {
  const urgency = urgencyOf(daysRemaining);
  return (
    <span className={`status-word status-${urgency}`}>
      {URGENCY_WORD[urgency]}
      <span className="muted"> · {describeRemaining(daysRemaining)}</span>
    </span>
  );
}
